'use client'

/**
 * The card a next step opens, under a Home answer (#16813, part of #16812).
 *
 * One step's label, its source as a small badge, the fields its draft can
 * edit, and two buttons: **Approve** and **Not now**. #16813 shipped the
 * generic `handoff` editor (recipient, subject, body); #16812's children add
 * one editor per draft kind they define — Meet's `agenda` editor (#16814) is
 * `./agenda-draft-editor.tsx`, and its heading follows the meeting picked;
 * `priority` (#16815) is `./priority-draft-editor`, and its approve links to
 * the Priority made; the Ask's `pulse` editor (who to ask, and 2–5
 * questions) is #16816's. An approved Ask saves a pulse request DRAFT and
 * links to its editor; the card says nothing was sent.
 *
 * ## Approve
 *
 * Calls `approveNextStep` with the draft AS EDITED. The server dispatches on
 * the step's type to a handler registry; a type with no handler answers
 * `unsupported`, and the card says so in words — "not available yet" — rather
 * than reporting success or failing silently. A turn whose unmet-need record
 * was not written has nothing to approve against, and answers the same way
 * without a round trip. Once approved, the card locks: the server makes a
 * retried approve a no-op, and the card makes it hard to send one. When the
 * handler says where the thing it wrote lives (Meet: the meeting), the card
 * links there. An agenda draft with no non-blank item cannot be approved.
 *
 * ## Not now
 *
 * Records the dismissal (fire-and-forget: telemetry about a choice is never
 * worth blocking the choice) and closes the card. Once the step is approved
 * the button reads **Close** and records nothing: a step is taken or
 * dismissed, never both.
 *
 * ## Route: Copy, not Approve (#16817)
 *
 * A Route draft is a request to whoever can act — the SIS administrator, HR,
 * SchoolAI. Nothing is sent by the product, so its card has no Approve:
 * **Copy** puts the draft AS EDITED on the clipboard and **Open in email**
 * is a `mailto:` link carrying the subject and body. Either one IS the
 * approve — it records the take once, with its channel — and the card says
 * plainly that nothing was sent. An optional picker over the org's people
 * prefills a colleague's name as the recipient; the role the draft was
 * addressed to stays on it, because the event carries the role, never a name.
 *
 * ## Reused by a use-case recommendation (#16826)
 *
 * A recommendation whose Start is a draft (a pulse request, a Priority)
 * opens this same card. It passes `onApprove`, which reaches the
 * recommendation's own action instead of `approveNextStep` (there may be no
 * unmet-need record on that turn), and `onDismiss`, which only closes: a
 * started recommendation is never also dismissed.
 *
 * ## Plain text, keyboard-operable
 *
 * The label, source and initial draft come from closed server vocabularies;
 * the edited draft is the asker's own text in form controls — nothing here is
 * markdown. Every field has a `<Label htmlFor>`, both buttons are native
 * buttons, and the result is announced through one `role="status"` region.
 * Semantic tokens only; never says "off-ramp".
 */

import type { Route } from 'next'
import Link from 'next/link'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'

import {
  approveNextStep,
  dismissNextStep,
  listAskAudienceOptions,
} from '@/app/actions/next-steps'
import { getAllPeopleForSelection } from '@/app/actions/people-selection'
import { AgendaDraftEditor } from '@/components/ai-chat/agenda-draft-editor'
import { Copy, Mail, Plus, X } from '@/components/icons'
import { Badge } from '@/components/ui/badge'
import { Button, buttonClassName } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  PersonPicker,
  type PersonPickerOption,
} from '@/components/ui/person-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { PriorityDraftEditor } from '@/components/ai-chat/priority-draft-editor'
import {
  agendaItemsToWrite,
  type Draft,
  type HandoffChannel,
  MAX_DRAFT_BODY_LENGTH,
  MAX_DRAFT_RECIPIENT_LENGTH,
  MAX_DRAFT_SUBJECT_LENGTH,
  isSameOriginPath,
  MAX_PULSE_DRAFT_QUESTION_LENGTH,
  MAX_PULSE_DRAFT_QUESTIONS,
  MIN_PULSE_DRAFT_QUESTIONS,
  NEXT_STEP_FAILED_MESSAGE,
  type NextStep,
  parseDraft,
  priorityDraftError,
} from '@/lib/ai-chat/next-steps'
import {
  handoffClipboardText,
  handoffMailtoHref,
} from '@/lib/ai-chat/route-draft'
import { writeToClipboard } from '@/lib/clipboard'

type OffRampStep = Extract<NextStep, { kind: 'offramp' }>
type HandoffDraft = Extract<Draft, { kind: 'handoff' }>

const DRAFT_CARD_APPROVED = 'Done — that step is on its way.'
// An Ask saves a draft and sends nothing (#16816): the card must not say
// "on its way" about a pulse nobody has sent yet.
const DRAFT_CARD_ASK_APPROVED =
  'Saved as a draft pulse request. Nothing has been sent.'
/** The Ask's link to the pulse request editor it saved a draft for. */
const DRAFT_CARD_ASK_LINK = 'Open it to review and send'
/** An approve that already made the thing it links to (#16815). */
const DRAFT_CARD_APPROVED_CREATED = 'Done — it is ready.'
/** The Plan step's link to the Priority it created (#16815). */
const DRAFT_CARD_PRIORITY_LINK = 'Open the priority'
/** The Meet step's link to the meeting it added to (#16814). */
const DRAFT_CARD_MEETING_LINK = 'Open the meeting'
/** Why Approve is off on an agenda draft with no non-blank item (#16814). */
const DRAFT_CARD_AGENDA_EMPTY = 'Add at least one agenda item to approve.'
/** The Monitor step's approve: a watch is set, nothing is made yet (#16819). */
const DRAFT_CARD_APPROVED_MONITOR =
  'Done — you will get a notification when it is live.'
// Promises nothing it does not do: an unsupported approve records nothing.
const DRAFT_CARD_UNSUPPORTED = 'This step is not available yet.'

/** What an Approve answers, from either action. */
export type DraftApproveResult = Awaited<ReturnType<typeof approveNextStep>>

type ApproveState =
  | { kind: 'idle' }
  | { kind: 'approving' }
  | { kind: 'approved'; href?: string }
  | { kind: 'unsupported' }
  | { kind: 'failed'; error: string }

/**
 * What the status line says. An agenda draft (#16814) names the meeting it
 * was added to; a pulse draft (#16816) is saved, never sent, so its approval
 * says so instead of "on its way".
 */
function statusText(
  state: ApproveState,
  step: OffRampStep,
  draft: Draft | undefined,
): string {
  switch (state.kind) {
    case 'approved':
      if (draft?.kind === 'agenda') {
        return `Added to the ${draft.meetingTitle} agenda.`
      }
      if (draft?.kind === 'pulse') return DRAFT_CARD_ASK_APPROVED
      if (state.href) return DRAFT_CARD_APPROVED_CREATED
      return step.type === 'monitor'
        ? DRAFT_CARD_APPROVED_MONITOR
        : DRAFT_CARD_APPROVED
    case 'unsupported':
      return DRAFT_CARD_UNSUPPORTED
    case 'failed':
      return state.error
    default:
      return ''
  }
}

// #16817 — Route. Each says what happened, and that the product sent nothing.
const ROUTE_COPIED =
  'Copied. Paste it into an email or message — nothing was sent.'
const ROUTE_OPENED =
  'Opened in your email app. Nothing is sent until you send it.'
const ROUTE_COPY_FAILED =
  'Could not copy. Select the message and copy it yourself.'

function HandoffEditor({
  draft,
  disabled,
  onChange,
  picker,
}: {
  draft: HandoffDraft
  disabled: boolean
  onChange: (draft: Draft) => void
  /** Route's optional colleague picker, under the To field. */
  picker?: ReactNode
}) {
  const baseId = useId()
  return (
    <div data-testid="ai-chat-draft-handoff" className="mt-3 space-y-2">
      <div className="space-y-1">
        <Label htmlFor={`${baseId}-recipient`}>To</Label>
        <Input
          id={`${baseId}-recipient`}
          value={draft.recipient}
          maxLength={MAX_DRAFT_RECIPIENT_LENGTH}
          disabled={disabled}
          onChange={event =>
            onChange({ ...draft, recipient: event.target.value })
          }
        />
      </div>
      {picker}
      <div className="space-y-1">
        <Label htmlFor={`${baseId}-subject`}>Subject</Label>
        <Input
          id={`${baseId}-subject`}
          value={draft.subject}
          maxLength={MAX_DRAFT_SUBJECT_LENGTH}
          disabled={disabled}
          onChange={event =>
            onChange({ ...draft, subject: event.target.value })
          }
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${baseId}-body`}>Message</Label>
        <Textarea
          id={`${baseId}-body`}
          value={draft.body}
          rows={4}
          maxLength={MAX_DRAFT_BODY_LENGTH}
          disabled={disabled}
          onChange={event => onChange({ ...draft, body: event.target.value })}
        />
      </div>
    </div>
  )
}

type PulseDraft = Extract<Draft, { kind: 'pulse' }>

/** What the picker says before a pick, when the asker has no unit (#16816). */
const ASK_PICK_AUDIENCE = 'Choose who to ask'

interface AudienceUnit {
  id: string
  name: string
}

/**
 * The Ask draft (#16816): who to ask, over the units the asker can see, and
 * the questions — 2 to 5, each editable. The drafted audience ("Your team")
 * stands until the units load; then the asker's own unit is picked unless
 * the draft already names one. A picker that cannot load leaves the drafted
 * audience in place, and the handler resolves it to the asker's own unit.
 */
function PulseEditor({
  draft,
  disabled,
  update,
}: {
  draft: PulseDraft
  disabled: boolean
  /** Apply an edit to the CURRENT draft — never to a stale copy. */
  update: (edit: (current: PulseDraft) => PulseDraft) => void
}) {
  const baseId = useId()
  const [units, setUnits] = useState<readonly AudienceUnit[]>([])
  // The asker has no unit of their own, so "Your team" would name nobody:
  // Approve would save the request with no audience. Say so instead.
  const [noOwnUnit, setNoOwnUnit] = useState(false)

  // Load once per card. The default is applied through `update`, so an
  // edit made while the units load is kept, and a unit already picked wins.
  useEffect(() => {
    let live = true
    listAskAudienceOptions()
      .then(result => {
        if (!live || !result.success) return
        setUnits(result.data.units)
        const own = result.data.units.find(
          unit => unit.id === result.data.defaultUnitId,
        )
        if (!own) {
          setNoOwnUnit(true)
          return
        }
        update(current =>
          current.audienceUnitId === undefined
            ? { ...current, audience: own.name, audienceUnitId: own.id }
            : current,
        )
      })
      .catch(() => {
        // The drafted audience stands; the handler resolves it.
      })
    return () => {
      live = false
    }
  }, [])

  function pickUnit(unitId: string) {
    const unit = units.find(candidate => candidate.id === unitId)
    if (!unit) return
    update(current => ({
      ...current,
      audience: unit.name,
      audienceUnitId: unit.id,
    }))
  }

  function setQuestion(index: number, text: string) {
    update(current => ({
      ...current,
      questions: current.questions.map((q, i) => (i === index ? text : q)),
    }))
  }

  function removeQuestion(index: number) {
    update(current => ({
      ...current,
      questions: current.questions.filter((_, i) => i !== index),
    }))
  }

  function addQuestion() {
    update(current => ({ ...current, questions: [...current.questions, ''] }))
  }

  const canRemove = draft.questions.length > MIN_PULSE_DRAFT_QUESTIONS
  const canAdd = draft.questions.length < MAX_PULSE_DRAFT_QUESTIONS

  return (
    <div data-testid="ai-chat-draft-pulse" className="mt-3 space-y-3">
      <div className="space-y-1">
        <Label htmlFor={`${baseId}-audience`}>Who to ask</Label>
        {units.length > 0 ? (
          <Select value={draft.audienceUnitId ?? ''} onValueChange={pickUnit}>
            <SelectTrigger
              id={`${baseId}-audience`}
              disabled={disabled}
              aria-label="Who to ask"
              className="pointer-coarse:min-h-11"
            >
              <SelectValue
                placeholder={
                  noOwnUnit && draft.audienceUnitId === undefined
                    ? ASK_PICK_AUDIENCE
                    : draft.audience
                }
              />
            </SelectTrigger>
            <SelectContent>
              {units.map(unit => (
                <SelectItem key={unit.id} value={unit.id}>
                  {unit.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input id={`${baseId}-audience`} value={draft.audience} readOnly />
        )}
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-text-primary">
          Questions
        </legend>
        <ol className="space-y-2">
          {draft.questions.map((question, index) => {
            const number = index + 1
            return (
              // Questions have no identity beyond their position.
              <li key={index} className="flex items-center gap-2">
                <Label htmlFor={`${baseId}-q${number}`} className="sr-only">
                  {`Question ${number}`}
                </Label>
                <Input
                  id={`${baseId}-q${number}`}
                  value={question}
                  maxLength={MAX_PULSE_DRAFT_QUESTION_LENGTH}
                  disabled={disabled}
                  onChange={event => setQuestion(index, event.target.value)}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="shrink-0 pointer-coarse:min-h-11 pointer-coarse:min-w-11"
                  aria-label={`Remove question ${number}`}
                  disabled={disabled || !canRemove}
                  onClick={() => removeQuestion(index)}
                >
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </li>
            )
          })}
        </ol>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="pointer-coarse:min-h-11"
          disabled={disabled || !canAdd}
          onClick={addQuestion}
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add a question
        </Button>
      </fieldset>
    </div>
  )
}

/**
 * Prefill the recipient from the org's people (#16817). Optional: when the
 * list cannot load, or is empty, the picker is simply not there and the To
 * field still takes anything. Choosing nobody puts the role back.
 */
function RoutePicker({
  draft,
  roleRecipient,
  disabled,
  onChange,
}: {
  draft: HandoffDraft
  /** The recipient the draft arrived with — the role's label. */
  roleRecipient: string
  disabled: boolean
  onChange: (draft: Draft) => void
}) {
  const pickerId = useId()
  const [people, setPeople] = useState<PersonPickerOption[]>([])

  useEffect(() => {
    let live = true
    getAllPeopleForSelection()
      .then(result => {
        if (live && result.success) setPeople(result.people)
      })
      .catch(() => {
        // The picker is a convenience; the To field still works without it.
      })
    return () => {
      live = false
    }
  }, [])

  if (people.length === 0) return null

  function pick(personId: string | null) {
    const { toPersonId: _previous, ...rest } = draft
    const person = people.find(candidate => candidate.id === personId)
    onChange(
      person
        ? {
            ...rest,
            recipient: (person.name || person.email).slice(
              0,
              MAX_DRAFT_RECIPIENT_LENGTH,
            ),
            toPersonId: person.id,
          }
        : { ...rest, recipient: roleRecipient },
    )
  }

  return (
    <div className="space-y-1">
      <Label htmlFor={pickerId}>Pick a colleague</Label>
      <PersonPicker
        id={pickerId}
        ariaLabel="Pick a colleague"
        value={draft.toPersonId ?? null}
        onChange={pick}
        people={people}
        placeholder="Optional"
        clearLabel="No one — keep the role"
        disabled={disabled}
      />
    </div>
  )
}

/**
 * Copy, Open in email, and Not now / Close (#16817). The first copy or open
 * records the take with its channel; later ones only copy or open. Recording
 * is fire-and-report: a copy that worked is reported as worked even if the
 * record did not land, and the next copy tries the record again.
 */
function RouteActions({
  draft,
  unmetNeedId,
  onClose,
}: {
  draft: HandoffDraft
  unmetNeedId: string | null
  onClose: () => void
}) {
  const [status, setStatus] = useState('')
  const [recorded, setRecorded] = useState(false)
  const recording = useRef(false)

  async function record(channel: HandoffChannel) {
    if (unmetNeedId === null || recorded || recording.current) return
    recording.current = true
    try {
      const result = await approveNextStep({
        unmetNeedId,
        type: 'route',
        channel,
        draft,
      })
      if (result.success) setRecorded(true)
    } catch {
      // The draft already left the card; the next copy records again — see sub-issue R carve-out
    } finally {
      recording.current = false
    }
  }

  function copy() {
    // Synchronous, in the click's own gesture — see `@/lib/clipboard`.
    if (!writeToClipboard(handoffClipboardText(draft))) {
      setStatus(ROUTE_COPY_FAILED)
      return
    }
    setStatus(ROUTE_COPIED)
    void record('copy')
  }

  function openInEmail() {
    setStatus(ROUTE_OPENED)
    void record('mailto')
  }

  function notNow() {
    // A take still being recorded is not dismissed: a step is taken or
    // dismissed, never both.
    if (unmetNeedId !== null && !recorded && !recording.current) {
      dismissNextStep({ unmetNeedId, type: 'route' }).catch(() => {
        // Recording the dismissal is not worth blocking it.
      })
    }
    onClose()
  }

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <Button
        type="button"
        size="sm"
        className="pointer-coarse:min-h-11"
        onClick={copy}
      >
        <Copy className="h-4 w-4" aria-hidden />
        Copy
      </Button>
      <a
        href={handoffMailtoHref(draft)}
        onClick={openInEmail}
        className={buttonClassName({
          size: 'sm',
          variant: 'outline',
          className: 'pointer-coarse:min-h-11',
        })}
      >
        <Mail className="h-4 w-4" aria-hidden />
        Open in email
      </a>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="pointer-coarse:min-h-11"
        onClick={notNow}
      >
        {recorded ? 'Close' : 'Not now'}
      </Button>
      <p
        role="status"
        aria-live="polite"
        className="text-sm text-text-secondary"
      >
        {status}
      </p>
    </div>
  )
}

export function DraftCard({
  step,
  unmetNeedId,
  onClose,
  onApprove,
  onDismiss,
}: {
  step: OffRampStep
  /** The turn's unmet-need record, or `null` when none was written. */
  unmetNeedId: string | null
  onClose: () => void
  /**
   * Approve through another action than `approveNextStep` (#16826: a
   * recommendation's draft start). Given, `unmetNeedId` is not needed.
   */
  onApprove?: (draft: Draft | undefined) => Promise<DraftApproveResult>
  /** Replaces recording the dismissal; the card still closes. */
  onDismiss?: () => void
}) {
  const headingId = useId()
  const emptyHintId = useId()
  const [draft, setDraft] = useState<Draft | undefined>(step.draft)
  const [state, setState] = useState<ApproveState>({ kind: 'idle' })

  const busy = state.kind === 'approving'
  const locked = state.kind === 'approved'
  // #16817 — a Route draft leaves by Copy or Open in email, never Approve.
  const isRoute = step.type === 'route'
  // An agenda draft whose rows are all blank has nothing to add (#16814).
  const empty =
    draft?.kind === 'agenda' && agendaItemsToWrite(draft).length === 0
  // An edited draft that would fail the server's validation is not sent: a
  // blank pulse question, say, or a priority with no title (#16815). The
  // server re-validates regardless.
  const invalid =
    draft !== undefined &&
    (parseDraft(draft) === null ||
      (draft.kind === 'priority' && priorityDraftError(draft) !== null))
  const heading =
    draft?.kind === 'agenda'
      ? `Add to the ${draft.meetingTitle} agenda`
      : step.label

  async function approve() {
    if (busy || locked || empty || invalid) return
    if (onApprove === undefined && unmetNeedId === null) {
      setState({ kind: 'unsupported' })
      return
    }
    setState({ kind: 'approving' })
    try {
      const result =
        onApprove !== undefined
          ? await onApprove(draft)
          : await approveNextStep({
              unmetNeedId: unmetNeedId ?? '',
              type: step.type,
              ...(draft ? { draft } : {}),
            })
      if (!result.success) {
        setState({
          kind: 'failed',
          error: result.error || NEXT_STEP_FAILED_MESSAGE,
        })
      } else if ('status' in result && result.status === 'unsupported') {
        setState({ kind: 'unsupported' })
      } else {
        const href =
          'href' in result &&
          typeof result.href === 'string' &&
          isSameOriginPath(result.href)
            ? result.href
            : undefined
        setState({ kind: 'approved', ...(href ? { href } : {}) })
      }
    } catch {
      // A rejected action must not strand the card — see sub-issue R carve-out
      setState({ kind: 'failed', error: NEXT_STEP_FAILED_MESSAGE })
    }
  }

  function notNow() {
    if (onDismiss !== undefined) {
      onDismiss()
    } else if (unmetNeedId !== null && !locked) {
      dismissNextStep({ unmetNeedId, type: step.type }).catch(() => {
        // Recording the dismissal is not worth blocking it.
      })
    }
    onClose()
  }

  return (
    <Card
      data-testid="ai-chat-draft-card"
      role="group"
      aria-labelledby={headingId}
      className="mt-3 border-stroke-extra-light bg-surface-panel p-3 shadow-none"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h4 id={headingId} className="text-sm font-semibold text-text-primary">
          {heading}
        </h4>
        <Badge variant="outline" className="text-xs font-medium">
          {step.source}
        </Badge>
      </div>

      {draft?.kind === 'handoff' && (
        <HandoffEditor
          draft={draft}
          disabled={busy || locked}
          onChange={setDraft}
          picker={
            isRoute ? (
              <RoutePicker
                draft={draft}
                roleRecipient={
                  step.draft?.kind === 'handoff' ? step.draft.recipient : ''
                }
                disabled={false}
                onChange={setDraft}
              />
            ) : undefined
          }
        />
      )}
      {draft?.kind === 'agenda' && (
        <AgendaDraftEditor
          draft={draft}
          disabled={busy || locked}
          onChange={setDraft}
        />
      )}
      {draft?.kind === 'priority' && (
        <PriorityDraftEditor
          draft={draft}
          disabled={busy || locked}
          onChange={setDraft}
        />
      )}
      {draft?.kind === 'pulse' && (
        <PulseEditor
          draft={draft}
          disabled={busy || locked}
          update={edit =>
            setDraft(current =>
              current?.kind === 'pulse' ? edit(current) : current,
            )
          }
        />
      )}

      {isRoute && draft?.kind === 'handoff' ? (
        <RouteActions
          draft={draft}
          unmetNeedId={unmetNeedId}
          onClose={onClose}
        />
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            type="button"
            size="sm"
            className="pointer-coarse:min-h-11"
            disabled={busy || locked || empty || invalid}
            aria-describedby={empty ? emptyHintId : undefined}
            onClick={approve}
          >
            {busy ? 'Approving…' : 'Approve'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="pointer-coarse:min-h-11"
            disabled={busy}
            onClick={notNow}
          >
            {locked ? 'Close' : 'Not now'}
          </Button>
          {empty && (
            <p id={emptyHintId} className="text-sm text-text-secondary">
              {DRAFT_CARD_AGENDA_EMPTY}
            </p>
          )}
          <p
            role="status"
            aria-live="polite"
            className="text-sm text-text-secondary"
          >
            {statusText(state, step, draft)}
          </p>
          {state.kind === 'approved' && state.href && (
            <Link
              href={state.href as Route}
              className="text-sm font-medium text-text-link underline-offset-4 hover:underline"
            >
              {draft?.kind === 'agenda'
                ? DRAFT_CARD_MEETING_LINK
                : draft?.kind === 'pulse'
                  ? DRAFT_CARD_ASK_LINK
                  : DRAFT_CARD_PRIORITY_LINK}
            </Link>
          )}
        </div>
      )}
    </Card>
  )
}
